import type { TFunction } from 'i18next';
import { u2000IsValidEmail } from '../../u2000Core/tools/U2000EmailHelper';
import { unmaskPhone, unmaskPostalCode } from '../../u2000Core/tools/U2000MaskUtils';
import type { U3101Employee } from '../U3101Service';

const NS = 'u3101';

export function u3101CollectEmployeeFormErrors(
  employee: U3101Employee,
  t: TFunction,
): Record<string, string> {
  const result: Record<string, string> = {};
  if (!employee.codeEmployee || employee.codeEmployee.trim() === '') {
    result.codeEmployee = t('errCodeEmployeeRequired', { ns: NS });
  }
  if (!employee.numEmployee || employee.numEmployee.trim() === '') {
    result.numEmployee = t('errNumEmployeeRequired', { ns: NS });
  }
  if (!employee.firstName || employee.firstName.trim() === '') {
    result.firstName = t('errFirstNameRequired', { ns: NS });
  }
  if (!employee.lastName || employee.lastName.trim() === '') {
    result.lastName = t('errLastNameRequired', { ns: NS });
  }
  if (!employee.addressLine1 || employee.addressLine1.trim() === '') {
    result.addressLine1 = t('errAddressLine1Required', { ns: NS });
  }
  if (!employee.city || employee.city.trim() === '') {
    result.city = t('errCityRequired', { ns: NS });
  }
  if (!employee.postalCode || employee.postalCode.trim() === '') {
    result.postalCode = t('errPostalCodeRequired', { ns: NS });
  } else {
    const unmaskedPostalCode = unmaskPostalCode(employee.postalCode);
    if (unmaskedPostalCode.length !== 6) {
      result.postalCode = t('errPostalCodeLength', { ns: NS });
    }
  }
  if (employee.phoneNumber && employee.phoneNumber.trim() !== '') {
    const unmaskedPhone = unmaskPhone(employee.phoneNumber);
    if (unmaskedPhone.length !== 10) {
      result.phoneNumber = t('errPhoneNumberLength', { ns: NS });
    }
  }
  if (employee.cellPhone && employee.cellPhone.trim() !== '') {
    const unmaskedCellPhone = unmaskPhone(employee.cellPhone);
    if (unmaskedCellPhone.length !== 10) {
      result.cellPhone = t('errPhoneNumberLength', { ns: NS });
    }
  }
  if (employee.otherPhone && employee.otherPhone.trim() !== '') {
    const unmaskedOtherPhone = unmaskPhone(employee.otherPhone);
    if (unmaskedOtherPhone.length !== 10) {
      result.otherPhone = t('errPhoneNumberLength', { ns: NS });
    }
  }
  if (employee.email && employee.email.trim() !== '' && !u2000IsValidEmail(employee.email)) {
    result.email = t('errEmailInvalid', { ns: NS });
  }
  if (employee.remunerationType !== 'Salary' && employee.remunerationType !== 'Hourly') {
    result.remunerationType = t('errRemunerationTypeRequired', { ns: NS });
  }
  if (employee.hourlyBase != null && employee.hourlyBase !== undefined) {
    if (employee.hourlyBase < 0) {
      result.hourlyBase = t('errHourlyBaseNegative', { ns: NS });
    } else if (employee.hourlyBase > 999999.99) {
      result.hourlyBase = t('errHourlyBaseRange', { ns: NS });
    }
  }
  if (employee.hourRate === undefined || employee.hourRate === null || employee.hourRate < 0) {
    result.hourRate = t('errHourRateRequired', { ns: NS });
  }
  if (employee.hourRate !== undefined && employee.hourRate !== null && employee.hourRate >= 0) {
    if (employee.hourRate > 999.99) {
      result.hourRate = t('errHourRateRange', { ns: NS });
    }
  }
  if (employee.vacationBncHours === undefined || employee.vacationBncHours === null) {
    result.vacationBncHours = t('errVacationBncHoursRequired', { ns: NS });
  }
  if (
    employee.vacationBncHoursInitial === undefined ||
    employee.vacationBncHoursInitial === null ||
    employee.vacationBncHoursInitial < 0
  ) {
    result.vacationBncHoursInitial = t('errVacationBncHoursInitialRequired', { ns: NS });
  }
  if (
    employee.vacationNegativeThresholdHours === undefined ||
    employee.vacationNegativeThresholdHours === null ||
    employee.vacationNegativeThresholdHours < 0
  ) {
    result.vacationNegativeThresholdHours = t('errVacationNegativeThresholdHoursRequired', { ns: NS });
  }
  if (employee.holidayBncHours === undefined || employee.holidayBncHours === null) {
    result.holidayBncHours = t('errHolidayBncHoursRequired', { ns: NS });
  }
  if (
    employee.holidayBncHoursInitial === undefined ||
    employee.holidayBncHoursInitial === null ||
    employee.holidayBncHoursInitial < 0
  ) {
    result.holidayBncHoursInitial = t('errHolidayBncHoursInitialRequired', { ns: NS });
  }
  if (employee.sickLeaveBncHours === undefined || employee.sickLeaveBncHours === null) {
    result.sickLeaveBncHours = t('errSickLeaveBncHoursRequired', { ns: NS });
  }
  if (
    employee.sickLeaveBncHoursInitial === undefined ||
    employee.sickLeaveBncHoursInitial === null ||
    employee.sickLeaveBncHoursInitial < 0
  ) {
    result.sickLeaveBncHoursInitial = t('errSickLeaveBncHoursInitialRequired', { ns: NS });
  }
  if (employee.specialBncHours === undefined || employee.specialBncHours === null) {
    result.specialBncHours = t('errSpecialBncHoursRequired', { ns: NS });
  }
  if (
    employee.specialBncHoursInitial === undefined ||
    employee.specialBncHoursInitial === null ||
    employee.specialBncHoursInitial < 0
  ) {
    result.specialBncHoursInitial = t('errSpecialBncHoursInitialRequired', { ns: NS });
  }
  if (!employee.country || !employee.country.id || employee.country.id === '') {
    result['country.id'] = t('errCountryRequired', { ns: NS });
  }
  if (!employee.state || !employee.state.id || employee.state.id === '') {
    result['state.id'] = t('errStateRequired', { ns: NS });
  }
  return result;
}
